/**
 * Canonical Slash Command Definitions for TerraMatrix AI Chat
 * Based on Commands_Master.md specification
 */

import {
  Component,
  ComponentType,
  Project,
  RefreshBadgeConfig,
} from "@/types";
import { getComponentDescendants } from "@/utils/componentHierarchy";
import { CommandResult, PropertyChange } from "./BaseCommandHandler";
import { v4 as uuidv4 } from "uuid";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useResponsiveStore } from "@/stores/editor/responsive-store";
import { templateInstantiationService } from "@/services/ai/TemplateInstantiationService";
import { ADD_COMPONENT_TYPE_MAP } from "./constants/add-component-type-map";
import {
  DEMO_TEMPLATE_COMMAND_DEFINITIONS,
  TEMPLATE_COMMAND_IMPORTERS,
  TemplateCommandOptions,
} from "./constants/demo-template-commands";
import {
  findNextContainerPosition,
  createAutoContainer,
  getTargetContainer,
} from "@/utils/componentPlacement";
import {
  createCompoundChart,
  isCompoundChartType,
  getChartDisplayName,
} from "./compoundChartFactory";
import {
  cloneTemplateComponents,
  getDefaultChartData,
  getDefaultProperties,
  getDefaultSize,
  getTemplateComponents,
  normalizeIconName,
} from "./utils/component-defaults";
import type { ComponentDefaultSourceContext } from "./utils/component-defaults";

export type CommandCategory = 'style' | 'layout' | 'component' | 'action' | 'data' | 'template';

export interface CommandParameter {
  name: string;
  type: 'string' | 'number' | 'color' | 'boolean' | 'position';
  description: string;
  required: boolean;
  defaultValue?: unknown;
}

export interface SlashCommandContext {
  activeProject?: Pick<Project, "id">;
  activePageId?: string;
  isCanvas?: boolean;
  selectedComponentIds?: string[];
  viewportBreakpoint?: 'mobile' | 'tablet' | 'desktop';
  canvasWidth?: number;
  sourceContext?: ComponentDefaultSourceContext;
}

export interface SlashCommand {
  name: string;
  aliases: string[];
  description: string;
  category: CommandCategory;
  parameters?: CommandParameter[];
  examples?: string[];
  handler: (
    args: string[],
    component?: Component,
    context?: SlashCommandContext,
  ) => Promise<CommandResult | PropertyChange> | CommandResult | PropertyChange;
}

// ===== HELPER FUNCTIONS =====

function parseColor(value: string): string {
  const colorMap: Record<string, string> = {
    'red': '#ef4444',
    'blue': '#3b82f6',
    'green': '#10b981',
    'yellow': '#eab308',
    'purple': '#a855f7',
    'pink': '#ec4899',
    'gray': '#6b7280',
    'grey': '#6b7280',
    'black': '#000000',
    'white': '#ffffff',
    'orange': '#f97316',
    'cyan': '#06b6d4',
    'indigo': '#6366f1',
  };

  return colorMap[value.toLowerCase()] || value;
}

function parsePosition(value: string): { x: number; y: number } | null {
  const match = value.match(/(\d+)[,\s]+(\d+)/);
  if (match) {
    return { x: parseInt(match[1], 10), y: parseInt(match[2], 10) };
  }
  return null;
}

function parseSize(value: string): string | number {
  if (value.endsWith('%')) {
    return value;
  }
  if (value.endsWith('px')) {
    return parseInt(value, 10);
  }
  if (value === 'full') {
    return '100%';
  }
  if (value === 'half') {
    return '50%';
  }
  if (value === 'auto') {
    return 'auto';
  }
  const num = parseInt(value, 10);
  return isNaN(num) ? value : num;
}

function resolvePageComponents(components: Component[], pageId: string | undefined): Component[] {
  if (components.length === 0) {
    return components;
  }

  const normalizedPageId = pageId ?? '';
  const filtered = components.filter(
    (component) => (component.pageId ?? '') === normalizedPageId,
  );

  if (filtered.length > 0) {
    return filtered;
  }

  return components;
}

function logPlacementDebug(_context: {
  label: string;
  selectedIds: string[];
  pageId?: string;
  componentType: ComponentType | string;
  targetContainerId?: string;
  targetContainerType?: ComponentType | string;
  pageComponentIds: string[];
  source?: 'canvas' | 'component';
  rootContainers?: Array<{ id: string; children: number }>;
}) {
  // Debug logging removed
}

function commandErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function commandError(error: string): CommandResult {
  return { success: false, action: 'unknown', error };
}

function commandMessage(message: string): CommandResult {
  return { success: true, action: 'help', message };
}

function isRecordArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(
    (item) => typeof item === "object" && item !== null && !Array.isArray(item),
  );
}

const COMPOUND_CHART_METADATA_PROPERTY_KEYS = [
  "dataBinding",
  "dataSource",
  "queryTemplateId",
  "writebackConfig",
] as const;

function getCompoundChartMetadataProperties(
  defaultProps: Record<string, unknown>,
): Record<string, unknown> {
  const metadataProperties: Record<string, unknown> = {};

  for (const key of COMPOUND_CHART_METADATA_PROPERTY_KEYS) {
    const value = defaultProps[key];
    if (value !== undefined) {
      metadataProperties[key] = value;
    }
  }

  return metadataProperties;
}

type SlashCommandHandler = SlashCommand["handler"];

function makeTemplateCommand(
  jsonPath: keyof typeof TEMPLATE_COMMAND_IMPORTERS,
  displayName: string,
  options?: TemplateCommandOptions & { commandName?: string },
): SlashCommandHandler {
  return async () => {
    const store = useEditorStore.getState();
    const project = store.project;
    const currentPage = store.currentPage;

    if (!project || !currentPage) {
      return {
        success: false,
        action: "template",
        error: "No active project or page. Please open a project first.",
      };
    }

    const errorName = options?.errorName ?? displayName;

    try {
      const templateModule = await TEMPLATE_COMMAND_IMPORTERS[jsonPath]();
      const template = templateModule.default;

      if (!template || !template.pages || !template.pages[0]?.components) {
        return {
          success: false,
          action: "template",
          error: "Invalid template structure",
        };
      }

      const templateComponents = getTemplateComponents(template.pages[0].components);
      const newComponents = cloneTemplateComponents(
        templateComponents,
        project.id,
        currentPage.id,
        options,
      );

      return {
        success: true,
        action: "add",
        components: newComponents,
        message: `Added ${displayName} (${newComponents.length} components)`,
      };
    } catch (error: unknown) {
      const logName = options?.commandName ?? displayName;
      console.error(`[${logName}] Error loading template:`, error);
      return {
        success: false,
        action: "template",
        error: commandErrorMessage(
          error,
          `Failed to load ${errorName} template`,
        ),
      };
    }
  };
}

// ===== CANONICAL COMMANDS =====

export const slashCommands: SlashCommand[] = [
  // ===== STYLE COMMANDS =====
  {
    name: 'color',
    aliases: ['c'],
    description: 'Apply text or fill color',
    category: 'style',
    parameters: [
      { name: 'value', type: 'color', description: 'Color name, hex code, or design token', required: true }
    ],
    examples: ['/color #3b82f6', '/c red'],
    handler: (args) => {
      return {
        style: { color: parseColor(args[0] || 'black') }
      };
    }
  },

  {
    name: 'background',
    aliases: ['bg'],
    description: 'Set background color or token',
    category: 'style',
    parameters: [
      { name: 'value', type: 'color', description: 'Color name, hex, gradient token', required: true }
    ],
    examples: ['/background slate-100', '/bg #f0f0f0'],
    handler: (args) => {
      return {
        style: { backgroundColor: parseColor(args[0] || 'transparent') }
      };
    }
  },

  {
    name: 'text-size',
    aliases: ['ts'],
    description: 'Set text size',
    category: 'style',
    parameters: [
      { name: 'value', type: 'string', description: 'px, rem, or size token', required: true }
    ],
    examples: ['/text-size 24px', '/ts 1.5rem'],
    handler: (args, component) => {
      if (component?.type === ComponentType.TEXT) {
        const value = args[0] || '16px';
        const size = value.match(/^\d+$/) ? parseInt(value, 10) : value;
        return { properties: { fontSize: size } };
      }
      return {};
    }
  },

  {
    name: 'width',
    aliases: ['w'],
    description: 'Set component width',
    category: 'style',
    parameters: [
      { name: 'value', type: 'string', description: 'px, %, or keywords (auto|full|half)', required: true }
    ],
    examples: ['/width 320px', '/w full', '/w 50%'],
    handler: (args, component) => {
      const width = parseSize(args[0] || '100%');

      // For containers with percentage widths, convert to pixels based on canvas width
      if (typeof width === 'string' && width.endsWith('%') && component) {
        const isContainer = component.type === ComponentType.FLEX_CONTAINER ||
                           component.type === ComponentType.CONTAINER;

        if (isContainer) {
          // Get canvas width from responsive store
          const responsiveState = useResponsiveStore.getState();
          const activeViewport = responsiveState.availableViewports.find(
            vp => vp.id === responsiveState.activeViewport
          );
          const canvasWidth = activeViewport?.width || 1920;
          const CANVAS_WRAPPER_PADDING = 8;
          const availableWidth = canvasWidth - (CANVAS_WRAPPER_PADDING * 2);

          // Convert percentage to pixels
          const percentage = parseFloat(width) / 100;
          const pixelWidth = Math.round(availableWidth * percentage);

          return { size: { width: pixelWidth } };
        }
      }

      // IMPORTANT: Always use 'size' for dimensions - components fill their wrapper
      if (typeof width === 'number') {
        return { size: { width } };
      }
      return { size: { width } };
    }
  },

  {
    name: 'height',
    aliases: ['h'],
    description: 'Set component height',
    category: 'style',
    parameters: [
      { name: 'value', type: 'string', description: 'px, %, or keywords', required: true }
    ],
    examples: ['/height 240', '/h auto'],
    handler: (args) => {
      const height = parseSize(args[0] || '100%');
      // IMPORTANT: Always use 'size' for dimensions - components fill their wrapper
      if (typeof height === 'number') {
        return { size: { height } };
      }
      return { size: { height } };
    }
  },

  {
    name: 'padding',
    aliases: ['pd'],
    description: 'Set padding',
    category: 'style',
    parameters: [
      { name: 'value', type: 'string', description: 'px, rem, or CSS shorthand', required: true }
    ],
    examples: ['/padding 16px', '/pd 1rem 2rem'],
    handler: (args) => {
      const value = args.join(' ');
      return {
        style: { padding: value.includes('px') || value.includes('rem') ? value : `${value}px` }
      };
    }
  },

  {
    name: 'margin',
    aliases: ['mg'],
    description: 'Set margin',
    category: 'style',
    parameters: [
      { name: 'value', type: 'string', description: 'px, rem, CSS shorthand, or auto', required: true }
    ],
    examples: ['/margin 0 auto', '/mg 16px'],
    handler: (args) => {
      const value = args.join(' ');
      return {
        style: { margin: value.includes('px') || value.includes('rem') || value === 'auto' ? value : `${value}px` }
      };
    }
  },

  {
    name: 'opacity',
    aliases: ['op'],
    description: 'Set opacity',
    category: 'style',
    parameters: [
      { name: 'percent', type: 'number', description: '0-100 or 0-1', required: true }
    ],
    examples: ['/opacity 65', '/op 0.5'],
    handler: (args) => {
      const value = parseFloat(args[0]) || 100;
      const opacity = value > 1 ? value / 100 : value;
      return { style: { opacity } };
    }
  },

  {
    name: 'radius',
    aliases: ['r'],
    description: 'Set border radius',
    category: 'style',
    parameters: [
      { name: 'value', type: 'string', description: 'px or presets (none|sm|md|lg|xl|full)', required: true }
    ],
    examples: ['/radius lg', '/r 8px'],
    handler: (args) => {
      const radiusMap: Record<string, string> = {
        'none': '0',
        'sm': '4px',
        'md': '8px',
        'lg': '12px',
        'xl': '16px',
        'full': '9999px'
      };
      const value = args[0] || 'md';
      return { style: { borderRadius: radiusMap[value] || value } };
    }
  },

  {
    name: 'border',
    aliases: ['bd'],
    description: 'Add or remove borders with subcommands',
    category: 'style',
    parameters: [
      { name: 'subcommand', type: 'string', description: 'width|style|color|radius|all|none or CSS shorthand', required: true },
      { name: 'value', type: 'string', description: 'Subcommand-specific value', required: false }
    ],
    examples: [
      '/border width 2px',
      '/border style solid',
      '/border color red',
      '/border radius 8px',
      '/border all 2px solid red',
      '/border none',
      '/bd 1px solid black'
    ],
    handler: (args, component) => {
      if (!args[0]) {
        return { style: { border: '1px solid #e5e7eb' } };
      }

      const subcommand = args[0]?.toLowerCase();
      const value = args.slice(1).join(' ');

      // Handle subcommands
      switch (subcommand) {
        case 'width':
          if (!value) return commandError('Border width requires a value (e.g., /border width 2px)');
          return { style: { borderWidth: value.includes('px') ? value : `${value}px` } };

        case 'style':
          if (!value) return commandError('Border style requires a value (e.g., /border style solid)');
          const validStyles = ['none', 'solid', 'dashed', 'dotted', 'double', 'groove', 'ridge', 'inset', 'outset'];
          if (!validStyles.includes(value.toLowerCase())) {
            return commandError(`Invalid border style. Use: ${validStyles.join(', ')}`);
          }
          return { style: { borderStyle: value } };

        case 'color':
          if (!value) return commandError('Border color requires a value (e.g., /border color red)');
          return { style: { borderColor: parseColor(value) } };

        case 'radius':
          if (!value) return commandError('Border radius requires a value (e.g., /border radius 8px)');
          const radiusMap: Record<string, string> = {
            'none': '0',
            'sm': '4px',
            'md': '8px',
            'lg': '12px',
            'xl': '16px',
            'full': '9999px'
          };
          const radiusValue = radiusMap[value.toLowerCase()] || value;
          return { style: { borderRadius: radiusValue.includes('px') ? radiusValue : `${radiusValue}px` } };

        case 'all':
          if (!value) return commandError('Border all requires values (e.g., /border all 2px solid red)');
          return { style: { border: value } };

        case 'none':
          return { style: { border: 'none' } };

        case 'default':
          return { style: { border: '1px solid #e5e7eb' } };

        default:
          // If not a subcommand, treat entire args as CSS shorthand
          return { style: { border: args.join(' ') } };
      }
    }
  },

  {
    name: 'shadow',
    aliases: ['sh'],
    description: 'Manage shadow with presets and subcommands',
    category: 'style',
    parameters: [
      { name: 'subcommand', type: 'string', description: 'preset|color|blur|spread|offset|custom|none or preset name', required: true },
      { name: 'value', type: 'string', description: 'Subcommand-specific value', required: false }
    ],
    examples: [
      '/shadow preset lg',
      '/shadow color red',
      '/shadow blur 10px',
      '/shadow spread 5px',
      '/shadow offset 10,10',
      '/shadow custom 0 4px 6px rgba(0,0,0,0.1)',
      '/shadow none',
      '/sh lg'
    ],
    handler: (args, component) => {
      if (!args[0]) {
        return { style: { boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' } };
      }

      const shadowMap: Record<string, string> = {
        'none': 'none',
        'sm': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        'md': '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        'lg': '0 10px 15px -3px rgb(0 0 0 / 0.1)',
        'xl': '0 20px 25px -5px rgb(0 0 0 / 0.1)'
      };

      const subcommand = args[0]?.toLowerCase();
      const value = args.slice(1).join(' ');

      // Handle subcommands
      switch (subcommand) {
        case 'preset':
          if (!value) return commandError('Shadow preset requires a value (e.g., /shadow preset lg)');
          return { style: { boxShadow: shadowMap[value.toLowerCase()] || shadowMap.md } };

        case 'color':
          if (!value) return commandError('Shadow color requires a value (e.g., /shadow color red)');
          // Get current shadow or use default
          const currentShadow = typeof component?.style?.boxShadow === 'string'
            ? component.style.boxShadow
            : '0 4px 6px -1px rgb(0 0 0 / 0.1)';
          // Parse and replace color in shadow
          const colorValue = parseColor(value);
          const shadowWithColor = currentShadow.replace(/rgb\([^)]+\)|rgba\([^)]+\)|#[0-9a-f]+/i, colorValue);
          return { style: { boxShadow: shadowWithColor } };

        case 'blur':
          if (!value) return commandError('Shadow blur requires a value (e.g., /shadow blur 10px)');
          const blurValue = value.includes('px') ? value : `${value}px`;
          return {
            properties: { shadowBlur: blurValue },
            style: { boxShadow: `0 4px ${blurValue} -1px rgb(0 0 0 / 0.1)` }
          };

        case 'spread':
          if (!value) return commandError('Shadow spread requires a value (e.g., /shadow spread 5px)');
          const spreadValue = value.includes('px') ? value : `${value}px`;
          return {
            properties: { shadowSpread: spreadValue },
            style: { boxShadow: `0 4px 6px ${spreadValue} rgb(0 0 0 / 0.1)` }
          };

        case 'offset':
          if (!value) return commandError('Shadow offset requires x,y values (e.g., /shadow offset 10,10)');
          const offset = parsePosition(value);
          if (!offset) return commandError('Invalid offset format. Use: /shadow offset 10,10');
          return {
            properties: { shadowOffsetX: `${offset.x}px`, shadowOffsetY: `${offset.y}px` },
            style: { boxShadow: `${offset.x}px ${offset.y}px 6px -1px rgb(0 0 0 / 0.1)` }
          };

        case 'custom':
          if (!value) return commandError('Shadow custom requires CSS shadow value (e.g., /shadow custom 0 4px 6px rgba(0,0,0,0.1))');
          return { style: { boxShadow: value } };

        case 'none':
          return {
            properties: { shadowBlur: undefined, shadowSpread: undefined, shadowOffsetX: undefined, shadowOffsetY: undefined },
            style: { boxShadow: 'none' }
          };

        default:
          // If not a subcommand, check if it's a preset
          if (shadowMap[subcommand]) {
            return { style: { boxShadow: shadowMap[subcommand] } };
          }
          // Otherwise treat as custom CSS shadow
          return { style: { boxShadow: args.join(' ') } };
      }
    }
  },

  {
    name: 'font',
    aliases: ['ft'],
    description: 'Adjust font properties',
    category: 'style',
    parameters: [
      { name: 'property', type: 'string', description: 'weight|style|size|family', required: true },
      { name: 'value', type: 'string', description: 'Property-specific value', required: true }
    ],
    examples: ['/font weight bold', '/ft style italic'],
    handler: (args) => {
      const property = args[0]?.toLowerCase();
      const value = args[1];

      switch (property) {
        case 'weight':
          return { properties: { fontWeight: value || 'normal' } };
        case 'style':
          return { properties: { fontStyle: value || 'normal' } };
        case 'size':
          const size = parseInt(value, 10);
          return { properties: { fontSize: isNaN(size) ? value : size } };
        case 'family':
          return { properties: { fontFamily: value || 'inherit' } };
        default:
          return {};
      }
    }
  },

  {
    name: 'scale',
    aliases: ['sc'],
    description: 'Apply incremental scaling',
    category: 'style',
    parameters: [
      { name: 'direction', type: 'string', description: 'up|down', required: true },
      { name: 'factor', type: 'number', description: 'Percent change, default 20%', required: false, defaultValue: 20 }
    ],
    examples: ['/scale up 20%', '/sc down'],
    handler: (args, component) => {
      const direction = args[0]?.toLowerCase();
      const factor = parseFloat(args[1]) || 20;
      const multiplier = direction === 'up' ? (1 + factor / 100) : (1 - factor / 100);

      if (component?.type === ComponentType.TEXT) {
        const currentSize = typeof component.properties?.fontSize === 'number'
          ? component.properties.fontSize
          : 16;
        return { properties: { fontSize: Math.round(currentSize * multiplier) } };
      }
      return { style: { transform: `scale(${multiplier})` } };
    }
  },

  {
    name: 'hover',
    aliases: ['hv'],
    description: 'Set hover state colors and effects',
    category: 'style',
    parameters: [
      { name: 'property', type: 'string', description: 'text|background|bg', required: true },
      { name: 'value', type: 'color', description: 'Color name, hex code, or design token', required: true }
    ],
    examples: ['/hover background blue', '/hv text white', '/hover bg #3b82f6'],
    handler: (args, component) => {
      if (component?.type !== ComponentType.BUTTON) {
        return {
          error: 'Hover styles can only be set on button components. Select a button first.'
        };
      }

      const property = args[0]?.toLowerCase();
      const value = parseColor(args[1] || '');

      if (property === 'background' || property === 'bg') {
        return { properties: { hoverBackgroundColor: value } };
      } else if (property === 'text' || property === 'color') {
        // Accept both 'text' (new) and 'color' (backward compatibility)
        return { properties: { hoverColor: value } };
      }

      return {
        error: `Unknown hover property: ${property}. Use 'text' or 'background'.`
      };
    }
  },

  // ===== LAYOUT COMMANDS =====
  {
    name: 'layout',
    aliases: ['ly'],
    description: 'Choose layout system',
    category: 'layout',
    parameters: [
      { name: 'type', type: 'string', description: 'flex|grid|stack', required: true },
      { name: 'options', type: 'string', description: 'e.g., flex row, column count', required: false }
    ],
    examples: ['/layout flex column', '/ly grid 3'],
    handler: (args) => {
      const type = args[0]?.toLowerCase();
      const option = args[1];

      if (type === 'flex') {
        const direction = option || 'row';
        return {
          style: { display: 'flex' },
          properties: { flexDirection: direction }
        };
      } else if (type === 'grid') {
        const columns = option || '2';
        return {
          style: {
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: '1rem'
          }
        };
      } else if (type === 'stack') {
        return {
          style: { display: 'flex' },
          properties: {
            flexDirection: option === 'horizontal' ? 'row' : 'column',
            gap: '1rem'
          }
        };
      }
      return {};
    }
  },

  {
    name: 'align',
    aliases: ['al'],
    description: 'Align text or elements',
    category: 'layout',
    parameters: [
      { name: 'target', type: 'string', description: 'text|items|content|self', required: true },
      { name: 'value', type: 'string', description: 'Alignment keyword', required: true }
    ],
    examples: ['/align text center', '/al items flex-start'],
    handler: (args, component) => {
      const target = args[0]?.toLowerCase();
      const value = args[1]?.toLowerCase();

      if (target === 'text') {
        return { properties: { textAlign: value } };
      }

      const alignMap: Record<string, string> = {
        'left': 'flex-start',
        'center': 'center',
        'right': 'flex-end',
        'justify': 'space-between',
        'start': 'flex-start',
        'end': 'flex-end'
      };

      if (target === 'items') {
        return { properties: { alignItems: alignMap[value] || value } };
      } else if (target === 'content') {
        return { properties: { justifyContent: alignMap[value] || value } };
      } else if (target === 'self') {
        return { style: { alignSelf: alignMap[value] || value } };
      }

      return {};
    }
  },

  {
    name: 'stack',
    aliases: ['st'],
    description: 'Stack children quickly',
    category: 'layout',
    parameters: [
      { name: 'direction', type: 'string', description: 'vertical|horizontal', required: false, defaultValue: 'vertical' }
    ],
    examples: ['/stack horizontal', '/st'],
    handler: (args) => {
      const direction = args[0]?.toLowerCase();
      const isHorizontal = direction === 'h' || direction === 'horizontal';
      return {
        style: { display: 'flex' },
        properties: {
          flexDirection: isHorizontal ? 'row' : 'column',
          gap: '1rem'
        }
      };
    }
  },

  {
    name: 'gap',
    aliases: ['gp'],
    description: 'Set spacing between children',
    category: 'layout',
    parameters: [
      { name: 'value', type: 'string', description: 'px or rem', required: true }
    ],
    examples: ['/gap 12px', '/gp 1rem'],
    handler: (args) => {
      const value = args[0] || '1rem';
      return {
        style: { gap: value.includes('px') || value.includes('rem') ? value : `${value}px` }
      };
    }
  },

  // ===== COMPONENT COMMAND =====
  {
    name: 'add',
    aliases: ['+'],
    description: 'Create a component or update a compatible selection',
    category: 'component',
    parameters: [
      { name: 'component', type: 'string', description: 'Catalog slug (text|button|input|image|card|container|file storage|radial dial|line chart|bar chart|pie chart|area chart|scatter chart|heatmap chart|treemap chart|gauge chart|vertical-gauge chart)', required: true },
      { name: 'options', type: 'string', description: 'Component-specific text, variant, data, or at <x>,<y> for placement', required: false }
    ],
    examples: ['/add text "Weekly Report"', '/+ line chart', '/add button "Submit"'],
    handler: async (args, component, context) => {
      const typeMap = ADD_COMPONENT_TYPE_MAP;

      // Try to match component type from args (handle multi-word types like "line chart")
      let typeArg = '';
      let componentType: ComponentType | undefined;
      let remainingArgs: string[] = [];

      // Try progressively longer combinations, longest match wins
      // (e.g., "icon" matches ICON, but "icon button" matches ICON_BUTTON)
      for (let i = 1; i <= Math.min(args.length, 3); i++) {
        const candidate = args.slice(0, i).join(' ').toLowerCase();
        if (typeMap[candidate]) {
          typeArg = candidate;
          componentType = typeMap[candidate];
          remainingArgs = args.slice(i);
        }
      }

      // If no match found, fall back to first arg
      if (!componentType) {
        typeArg = args[0]?.toLowerCase() || '';
        remainingArgs = args.slice(1);
      }

      let content = remainingArgs[0] || '';
      let explicitPosition: { x: number; y: number } | undefined;

      // Check for position argument (at x,y)
      const atIndex = remainingArgs.findIndex(arg => arg.toLowerCase() === 'at');
      if (atIndex !== -1 && remainingArgs[atIndex + 1]) {
        explicitPosition = parsePosition(remainingArgs[atIndex + 1]) || undefined;
        // Remove position args from content
        remainingArgs = remainingArgs.slice(0, atIndex);
        content = remainingArgs.slice(1).join(' ');
      }
      if (!componentType) {
        return {
          success: false,
          action: 'add',
          error: `Unknown component type: ${typeArg}. Use: text, data text, button, input, image, icon, card, container, table, advanced table, file storage, kanban board, gantt chart, calendar, radial dial, toggle switch, status light, push button, icon button, stepper, line chart, bar chart, pie chart, area chart, scatter chart, heatmap chart, treemap chart, gauge chart, progress ring, progress bar, milestone tracker, vertical-gauge chart, map chart, modal, checklist, timeline, assignment widget, project hub, questionnaire, voting card`
        };
      }

      // If we have a selected component of the same type, modify it
      if (component && component.type === componentType && content) {
        if (componentType === ComponentType.TEXT) {
          return { properties: { content } };
        } else if (componentType === ComponentType.BUTTON) {
          return { properties: { text: content } };
        }
      }

      // Get editor state for adding new component
      const store = useEditorStore.getState();
      const components = store.components;
      const selectedIds = store.selectedComponents || [];
      const project = context?.activeProject ?? store.project;
      const pageId = context?.activePageId ?? store.currentPageId;
      const projectId = project?.id;

      if (!projectId || !pageId) {
        return {
          success: false,
          action: 'add',
          error: 'Active project and page context are required to add components',
        };
      }

      const pageComponents = resolvePageComponents(components, pageId);
      const selectedContainer = getTargetContainer(selectedIds, pageComponents);

      const rootContainers = pageComponents
        .filter(c => !c.parentId && (c.type === ComponentType.FLEX_CONTAINER || c.type === ComponentType.CONTAINER))
        .map(c => ({ id: c.id, children: c.children?.length || 0 }));

      logPlacementDebug({
        label: `add-${typeArg}`,
        selectedIds,
        pageId,
        componentType,
        targetContainerId: selectedContainer?.id,
        targetContainerType: selectedContainer?.type,
        pageComponentIds: pageComponents.map(c => c.id),
        rootContainers,
        source: context?.isCanvas ? 'canvas' : 'component'
      });

      const sourceContext = context?.sourceContext;
      const isBindingModeCreation = sourceContext?.mode === "binding";
      let defaultProps: ReturnType<typeof getDefaultProperties>;
      try {
        defaultProps = getDefaultProperties(componentType, content, {
          sourceContext,
        });
      } catch (error) {
        return {
          success: false,
          action: 'add',
          error: commandErrorMessage(
            error,
            'Component creation requires valid data binding context',
          ),
        };
      }

      // Set displayMode based on slash command alias for gauge variants
      if (componentType === ComponentType.GAUGE_CHART) {
        if (typeArg === 'progress ring' || typeArg === 'completion ring' || typeArg === 'circular progress') {
          defaultProps.displayMode = 'ring';
        } else if (typeArg === 'progress bar') {
          defaultProps.displayMode = 'bar';
        } else if (typeArg === 'milestone tracker' || typeArg === 'milestone') {
          defaultProps.displayMode = 'milestone';
        }
      }

      // Handle chart components with special sizing
      const isChart = [
        ComponentType.CHART,
        ComponentType.LINE_CHART,
        ComponentType.BAR_CHART,
        ComponentType.PIE_CHART,
        ComponentType.AREA_CHART,
        ComponentType.SCATTER_CHART,
        ComponentType.HEATMAP_CHART,
        ComponentType.TREEMAP_CHART,
        ComponentType.GAUGE_CHART,
        ComponentType.VERTICAL_GAUGE_CHART,
        ComponentType.MAP_CHART,
      ].includes(componentType);

      if (isChart) {
        defaultProps.title = `${typeArg.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`;
        if (!isBindingModeCreation) {
          defaultProps.data = getDefaultChartData(componentType);
        }
        defaultProps.flex = '1 1 auto';

        // Handle area chart stacked modifier
        if (componentType === ComponentType.AREA_CHART) {
          const isStacked = remainingArgs.some(arg => arg.toLowerCase() === 'stacked');
          if (isStacked) {
            defaultProps.stacked = true;
          }
        }
      }

      // ═══════════════════════════════════════════════════════════════════════
      // COMPOUND CHART CREATION
      // ═══════════════════════════════════════════════════════════════════════
      // For line, bar, and area charts, create compound layouts by default
      // (wrapper + header with title + live value display + chart)
      if (isCompoundChartType(componentType)) {
        try {
          const rawTitle = defaultProps.title;
          const chartTitle =
            typeof rawTitle === 'string' ? rawTitle : getChartDisplayName(componentType);

          const rawData = defaultProps.data;
          const chartData = isRecordArray(rawData) ? rawData : undefined;

          const compoundResult = createCompoundChart(componentType, {
            title: chartTitle,
            data: isBindingModeCreation ? undefined : chartData,
            omitDefaultData: isBindingModeCreation,
            pageId,
            parentId: selectedContainer?.id,
            chartProperties: {
              ...getCompoundChartMetadataProperties(defaultProps),
              stacked: defaultProps.stacked,
            },
          });

          // If adding to a selected container
          if (selectedContainer) {
            // Update the wrapper's parentId
            compoundResult.wrapper.parentId = selectedContainer.id;

            return {
              success: true,
              action: 'add',
              components: compoundResult.components,
              message: `Added compound ${typeArg} to container`,
            };
          }

          // Auto-create container for compound chart
          if (!explicitPosition) {
            const pendingPositions = store.getPendingContainerPositions();
            const containerPosition = findNextContainerPosition(pageComponents, pendingPositions);

            const responsiveState = useResponsiveStore.getState();
            const activeViewport = responsiveState.availableViewports.find(
              vp => vp.id === responsiveState.activeViewport
            );
            const canvasWidth = activeViewport?.width || 1920;

            // Compound chart wrapper is 300px height by default
            const newContainer = createAutoContainer(
              containerPosition,
              projectId,
              pageId,
              canvasWidth,
              300
            );

            // Update wrapper to be child of container
            compoundResult.wrapper.parentId = newContainer.id;
            newContainer.children = [compoundResult.wrapper.id];

            // Register pending container
            const containerSize = newContainer.size || { width: canvasWidth - 16, height: 350 };
            if (newContainer.id == null) {
              throw new Error('registerPendingContainer: container was created without an id');
            }
            store.registerPendingContainer(newContainer.id, containerPosition, containerSize);
            store.cleanupStalePendingContainers();

            return {
              success: true,
              action: 'add',
              components: [newContainer, ...compoundResult.components],
              containerId: newContainer.id,
              message: `Added compound ${typeArg} in new container`,
            };
          }

          // Explicit position - just return compound components without additional container
          return {
            success: true,
            action: 'add',
            components: compoundResult.components,
            message: `Added compound ${typeArg}`,
          };
        } catch (error) {
          // If compound chart creation fails, fall through to standard chart creation
          console.warn('Compound chart creation failed, falling back to standard chart:', error);
        }
      }

      const rawDisplayMode = defaultProps.displayMode;
      const displayMode = typeof rawDisplayMode === 'string' ? rawDisplayMode : undefined;
      const defaultSize = getDefaultSize(componentType, displayMode);
      // For charts, derive style height from size.height to ensure consistency
      // This prevents the "overflow clipping" bug where style.height > size.height
      // NOTE: No minWidth - charts should be resizable to any width via AI commands
      const defaultStyle = isChart ? {
        width: '100%',
        height: `${defaultSize.height}px`,
        minHeight: `${defaultSize.height}px`
      } : {};

      // For charts, use the size height for container sizing (now consistent with style.height)
      const actualContentHeight = defaultSize.height;
      const isContainerComponent =
        componentType === ComponentType.FLEX_CONTAINER ||
        componentType === ComponentType.CONTAINER;

      if (isContainerComponent) {
        const responsiveState = useResponsiveStore.getState();
        const activeViewport = responsiveState.availableViewports.find(
          vp => vp.id === responsiveState.activeViewport
        );
        const canvasWidth = activeViewport?.width || 1920;
        const containerPosition = explicitPosition
          ? { ...explicitPosition, z: 1 }
          : findNextContainerPosition(
              pageComponents,
              store.getPendingContainerPositions(),
            );
        const newContainer = createAutoContainer(
          containerPosition,
          projectId,
          pageId,
          canvasWidth,
          defaultSize.height,
        );

        newContainer.name = `${typeArg || 'container'}-${Date.now()}`;
        newContainer.properties = {
          ...newContainer.properties,
          ...defaultProps,
        };
        newContainer.style = {
          ...newContainer.style,
          ...defaultStyle,
        };
        newContainer.children = [];

        const containerSize = newContainer.size || { width: canvasWidth - 16, height: defaultSize.height };
        if (!explicitPosition) {
          if (newContainer.id == null) {
            throw new Error('registerPendingContainer: container was created without an id');
          }
          store.registerPendingContainer(newContainer.id, containerPosition, containerSize);
          store.cleanupStalePendingContainers();
        }

        return {
          success: true,
          action: 'add',
          components: [newContainer],
          containerId: !explicitPosition ? newContainer.id : undefined,
          message: explicitPosition
            ? `Added ${typeArg} at position`
            : `Added ${typeArg} container`,
        };
      }

      if (selectedContainer) {
        // Add to selected container
        return {
          success: true,
          action: 'add',
          component: {
            id: uuidv4(),
            type: componentType,
            name: `${typeArg}-${Date.now()}`,
            parentId: selectedContainer.id,
            properties: defaultProps,
            size: defaultSize,
            style: defaultStyle,
            locked: false,
            hidden: false
          },
          message: `Added ${typeArg} to container`
        };
      } else if (!explicitPosition) {
        // Auto-create container (only if no explicit position provided)
        // Get pending container positions to avoid overlap with in-flight containers
        const pendingPositions = store.getPendingContainerPositions();
        const containerPosition = findNextContainerPosition(pageComponents, pendingPositions);

        // Get canvas width from responsive store for full-width containers
        const responsiveState = useResponsiveStore.getState();
        const activeViewport = responsiveState.availableViewports.find(
          vp => vp.id === responsiveState.activeViewport
        );
        const canvasWidth = activeViewport?.width || 1920; // Default to desktop width

        // Pass component height to create appropriately sized container
        // Use actualContentHeight which accounts for chart's style.height override
        const newContainer = createAutoContainer(
          containerPosition,
          projectId,
          pageId,
          canvasWidth,
          actualContentHeight // Pass the actual rendered height for accurate container sizing
        );
        const componentId = uuidv4();

        const newComponent = {
          id: componentId,
          type: componentType,
          name: `${typeArg}-${Date.now()}`,
          parentId: newContainer.id,
          properties: defaultProps,
          size: defaultSize,
          style: defaultStyle,
          locked: false,
          hidden: false
        };

        newContainer.children = [componentId];

        // Register this container as pending to prevent overlap with next command
        // Pass the actual container size so next position calculation is accurate
        const containerSize = newContainer.size || { width: canvasWidth - 16, height: 450 };
        console.log('📦 [PENDING_CONTAINER] Registering:', {
          id: newContainer.id,
          position: containerPosition,
          size: containerSize,
          contentHeight: actualContentHeight
        });
        if (newContainer.id == null) {
          throw new Error('registerPendingContainer: container was created without an id');
        }
        store.registerPendingContainer(newContainer.id, containerPosition, containerSize);

        // Clean up stale pending containers (older than 5 seconds)
        store.cleanupStalePendingContainers();

        return {
          success: true,
          action: 'add',
          components: [newContainer, newComponent],
          containerId: newContainer.id, // Include container ID for cleanup
          message: `Added ${typeArg} in new container`
        };
      } else {
        // Explicit position provided - use it without container
        return {
          success: true,
          action: 'add',
          component: {
            id: uuidv4(),
            type: componentType,
            name: `${typeArg}-${Date.now()}`,
            properties: defaultProps,
            position: explicitPosition,
            size: defaultSize,
            style: defaultStyle,
            locked: false,
            hidden: false
          },
          message: `Added ${typeArg} at position`
        };
      }
    }
  },

  {
    name: 'text',
    aliases: ['txt'],
    description: 'Set text content for text or button components',
    category: 'component',
    parameters: [
      { name: 'value', type: 'string', description: 'Text content to display', required: true }
    ],
    examples: ['/text "Click Here"', '/txt Submit', '/text "Welcome to TerraMatrix"'],
    handler: (args, component) => {
      if (!component) {
        return { success: false, action: 'modify' as const, error: 'No component selected' };
      }

      const textValue = args.join(' ').replace(/^["']|["']$/g, ''); // Remove surrounding quotes

      if (component.type === ComponentType.TEXT) {
        return { properties: { content: textValue } };
      } else if (component.type === ComponentType.BUTTON) {
        return { properties: { text: textValue } };
      }

      return {
        error: `Text command only works with text and button components. Selected component is ${component.type}`
      };
    }
  },

  // ===== ICON COMMANDS =====
  {
    name: 'icon',
    aliases: ['ic'],
    description: 'Change the icon for an icon component',
    category: 'component',
    parameters: [
      { name: 'name', type: 'string', description: 'Icon name (e.g., Heart, Users, Star)', required: true }
    ],
    examples: ['/icon Heart', '/ic Users', '/icon ArrowRight'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon command only works with icon components. Select an icon first.'
        };
      }

      const rawInput = args.join(' ').replace(/^["']|["']$/g, ''); // Remove surrounding quotes
      if (!rawInput) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Please specify an icon name: /icon <name>'
        };
      }

      // Normalize to PascalCase for Lucide/Radix compatibility
      const iconName = normalizeIconName(rawInput);

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { iconName }
        },
        message: `✅ Changed icon to ${iconName}`
      };
    }
  },

  {
    name: 'icon-size',
    aliases: ['ics'],
    description: 'Set icon size',
    category: 'component',
    parameters: [
      { name: 'value', type: 'number', description: 'Size in pixels (16-128)', required: true }
    ],
    examples: ['/icon-size 32', '/ics 48', '/icon-size 64'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon size command only works with icon components. Select an icon first.'
        };
      }

      const size = parseInt(args[0], 10);
      if (isNaN(size) || size < 16 || size > 128) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon size must be between 16 and 128 pixels'
        };
      }

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { size }
        },
        message: `✅ Set icon size to ${size}px`
      };
    }
  },

  {
    name: 'icon-color',
    aliases: ['icc'],
    description: 'Set icon color',
    category: 'component',
    parameters: [
      { name: 'value', type: 'color', description: 'Color name, hex code, or design token', required: true }
    ],
    examples: ['/icon-color red', '/icc #3b82f6', '/icon-color blue'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon color command only works with icon components. Select an icon first.'
        };
      }

      const color = parseColor(args[0] || '#000000');
      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { color }
        },
        message: `✅ Set icon color to ${color}`
      };
    }
  },

  {
    name: 'icon-rotate',
    aliases: ['icr'],
    description: 'Rotate icon',
    category: 'component',
    parameters: [
      { name: 'degrees', type: 'number', description: 'Rotation in degrees (0-360)', required: true }
    ],
    examples: ['/icon-rotate 45', '/icr 90', '/icon-rotate 180'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon rotate command only works with icon components. Select an icon first.'
        };
      }

      const rotation = parseInt(args[0], 10);
      if (isNaN(rotation)) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Rotation must be a number (0-360 degrees)'
        };
      }

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { rotation: rotation % 360 }
        },
        message: `✅ Rotated icon to ${rotation % 360}°`
      };
    }
  },

  {
    name: 'icon-library',
    aliases: ['icl'],
    description: 'Switch icon library',
    category: 'component',
    parameters: [
      { name: 'library', type: 'string', description: 'lucide or radix', required: true }
    ],
    examples: ['/icon-library lucide', '/icl radix'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon library command only works with icon components. Select an icon first.'
        };
      }

      const library = args[0]?.toLowerCase();
      if (library !== 'lucide' && library !== 'radix') {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Library must be either "lucide" or "radix"'
        };
      }

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { library }
        },
        message: `✅ Switched to ${library === 'lucide' ? 'Lucide' : 'Radix'} library`
      };
    }
  },

  {
    name: 'icon-stroke',
    aliases: ['icsw'],
    description: 'Set icon stroke width (Lucide icons only)',
    category: 'component',
    parameters: [
      { name: 'value', type: 'number', description: 'Stroke width (0.5-4)', required: true }
    ],
    examples: ['/icon-stroke 2', '/icsw 3', '/icon-stroke 1.5'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon stroke command only works with icon components. Select an icon first.'
        };
      }

      const library = typeof component.properties?.library === 'string' ? component.properties.library : undefined;
      if (library !== 'lucide') {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Stroke width only applies to Lucide icons. Switch to Lucide library first.'
        };
      }

      const strokeWidth = parseFloat(args[0]);
      if (isNaN(strokeWidth) || strokeWidth < 0.5 || strokeWidth > 4) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Stroke width must be between 0.5 and 4'
        };
      }

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { strokeWidth }
        },
        message: `✅ Set stroke width to ${strokeWidth}`
      };
    }
  },

  {
    name: 'icon-opacity',
    aliases: ['ico'],
    description: 'Set icon opacity',
    category: 'component',
    parameters: [
      { name: 'value', type: 'number', description: 'Opacity (0-100 or 0-1)', required: true }
    ],
    examples: ['/icon-opacity 50', '/ico 0.5', '/icon-opacity 75'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon opacity command only works with icon components. Select an icon first.'
        };
      }

      const value = parseFloat(args[0]);
      if (isNaN(value)) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Opacity must be a number (0-100 or 0-1)'
        };
      }

      const opacity = value > 1 ? value / 100 : value;
      if (opacity < 0 || opacity > 1) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Opacity must be between 0 and 1 (or 0-100)'
        };
      }

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { opacity }
        },
        message: `✅ Set opacity to ${Math.round(opacity * 100)}%`
      };
    }
  },

  {
    name: 'icon-fill',
    aliases: ['icf'],
    description: 'Toggle icon fill mode (Lucide icons only)',
    category: 'component',
    parameters: [
      { name: 'state', type: 'string', description: 'on or off', required: true }
    ],
    examples: ['/icon-fill on', '/icf off'],
    handler: (args, component) => {
      if (!component || component.type !== ComponentType.ICON) {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Icon fill command only works with icon components. Select an icon first.'
        };
      }

      const library = typeof component.properties?.library === 'string' ? component.properties.library : undefined;
      if (library !== 'lucide') {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Fill mode only applies to Lucide icons. Switch to Lucide library first.'
        };
      }

      const state = args[0]?.toLowerCase();
      if (state !== 'on' && state !== 'off') {
        return {
          success: false,
          action: 'modify' as const,
          error: 'Fill state must be either "on" or "off"'
        };
      }

      return {
        success: true,
        action: 'modify' as const,
        changes: {
          properties: { fill: state === 'on' }
        },
        message: `✅ Icon fill ${state === 'on' ? 'enabled' : 'disabled'}`
      };
    }
  },

  // ===== FILE STORAGE COMMANDS =====
  {
    name: 'file-storage',
    aliases: ['fs', 'files'],
    description: 'Configure file storage component properties',
    category: 'component',
    parameters: [
      { name: 'property', type: 'string', description: 'Property to set: title, mode (grid|list), types, uploads (on|off), delete (on|off), search (on|off), thumbnails (on|off), quota (on|off), max-size, scope', required: true },
      { name: 'value', type: 'string', description: 'Value to set', required: false }
    ],
    examples: [
      '/file-storage title "Project Documents"',
      '/fs mode grid',
      '/fs mode list',
      '/fs types images',
      '/fs types documents',
      '/fs uploads off',
      '/fs max-size 25',
      '/files scope project',
    ],
    handler: (args, component) => {
      if (!component) {
        return { success: false, action: 'modify' as const, error: 'No component selected. Select a file storage component first.' };
      }
      if (component.type !== ComponentType.FILE_STORAGE) {
        return { success: false, action: 'modify' as const, error: `This command only works with file storage components. Selected component is ${component.type}` };
      }

      const property = args[0]?.toLowerCase();
      const value = args.slice(1).join(' ').replace(/^["']|["']$/g, '');

      if (!property) {
        return { success: false, action: 'modify' as const, error: 'Specify a property: title, mode, types, uploads, delete, search, thumbnails, quota, max-size, scope' };
      }

      const ok = (props: Record<string, unknown>, msg: string) => ({
        success: true as const,
        action: 'modify' as const,
        changes: { properties: props },
        message: msg,
      });
      const fail = (msg: string) => ({
        success: false as const,
        action: 'modify' as const,
        error: msg,
      });

      switch (property) {
        case 'title':
        case 'name':
          if (!value) return fail('Provide a title value');
          return ok({ title: value }, `Title set to "${value}"`);

        case 'mode':
        case 'display':
        case 'view': {
          const mode = value.toLowerCase();
          if (mode === 'grid' || mode === 'list') {
            return ok({ displayMode: mode }, `Display mode set to ${mode}`);
          }
          return fail('Display mode must be "grid" or "list"');
        }

        case 'types':
        case 'file-types':
        case 'filetypes': {
          const presets: Record<string, string[]> = {
            'images': ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
            'documents': ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'],
            'spreadsheets': ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
            'media': ['image/*', 'video/*', 'audio/*'],
            'all': [],
          };
          const preset = presets[value.toLowerCase()];
          if (preset !== undefined) {
            return ok({ allowedFileTypes: preset }, `File types set to ${value}`);
          }
          // Treat as comma-separated MIME types
          const types = value.split(',').map(t => t.trim());
          return ok({ allowedFileTypes: types }, `File types set to ${types.join(', ')}`);
        }

        case 'uploads':
        case 'upload': {
          const on = ['on', 'true', 'yes', 'enable', 'enabled'].includes(value.toLowerCase());
          const off = ['off', 'false', 'no', 'disable', 'disabled'].includes(value.toLowerCase());
          if (!on && !off) return fail('Use "on" or "off"');
          return ok({ uploadEnabled: on }, `Uploads ${on ? 'enabled' : 'disabled'}`);
        }

        case 'delete':
        case 'deleting': {
          const on = ['on', 'true', 'yes', 'enable', 'enabled'].includes(value.toLowerCase());
          const off = ['off', 'false', 'no', 'disable', 'disabled'].includes(value.toLowerCase());
          if (!on && !off) return fail('Use "on" or "off"');
          return ok({ deleteEnabled: on }, `Delete ${on ? 'enabled' : 'disabled'}`);
        }

        case 'search': {
          const on = ['on', 'true', 'yes', 'enable', 'enabled'].includes(value.toLowerCase());
          const off = ['off', 'false', 'no', 'disable', 'disabled'].includes(value.toLowerCase());
          if (!on && !off) return fail('Use "on" or "off"');
          return ok({ showSearch: on }, `Search ${on ? 'shown' : 'hidden'}`);
        }

        case 'thumbnails':
        case 'thumbs': {
          const on = ['on', 'true', 'yes', 'enable', 'enabled'].includes(value.toLowerCase());
          const off = ['off', 'false', 'no', 'disable', 'disabled'].includes(value.toLowerCase());
          if (!on && !off) return fail('Use "on" or "off"');
          return ok({ showThumbnails: on }, `Thumbnails ${on ? 'shown' : 'hidden'}`);
        }

        case 'quota':
        case 'quota-bar': {
          const on = ['on', 'true', 'yes', 'enable', 'enabled'].includes(value.toLowerCase());
          const off = ['off', 'false', 'no', 'disable', 'disabled'].includes(value.toLowerCase());
          if (!on && !off) return fail('Use "on" or "off"');
          return ok({ showQuotaBar: on }, `Quota bar ${on ? 'shown' : 'hidden'}`);
        }

        case 'max-size':
        case 'maxsize':
        case 'size-limit': {
          const mb = parseInt(value, 10);
          if (isNaN(mb) || mb < 1) return fail('Provide max file size in MB (e.g., 25)');
          return ok({ maxFileSize: mb * 1024 * 1024 }, `Max file size set to ${mb}MB`);
        }

        case 'scope': {
          const scope = value.toLowerCase();
          if (['project', 'organization', 'component'].includes(scope)) {
            return ok({ defaultScope: scope }, `Default scope set to ${scope}`);
          }
          return fail('Scope must be "project", "organization", or "component"');
        }

        default:
          return fail(`Unknown file-storage property: ${property}. Use: title, mode, types, uploads, delete, search, thumbnails, quota, max-size, scope`);
      }
    }
  },

  // ===== ACTION COMMANDS =====
  {
    name: 'visibility',
    aliases: ['vis'],
    description: 'Toggle component visibility',
    category: 'action',
    parameters: [
      { name: 'state', type: 'string', description: 'show|hide|toggle', required: true }
    ],
    examples: ['/visibility hide', '/vis show'],
    handler: (args) => {
      const state = args[0]?.toLowerCase();
      if (state === 'hide') {
        return { style: { display: 'none' } };
      } else if (state === 'show') {
        return { style: { display: 'block' } };
      }
      // Toggle would require component state
      return {};
    }
  },

  {
    name: 'lock',
    aliases: ['lk'],
    description: 'Manage locked state',
    category: 'action',
    parameters: [
      { name: 'state', type: 'string', description: 'on|off|toggle', required: false, defaultValue: 'toggle' }
    ],
    examples: ['/lock on', '/lk off'],
    handler: (args) => {
      const state = args[0]?.toLowerCase();
      if (state === 'on') {
        return { properties: { locked: true } };
      } else if (state === 'off') {
        return { properties: { locked: false } };
      }
      // Toggle would require component state
      return {};
    }
  },

  {
    name: 'delete',
    aliases: ['rm'],
    description: 'Delete the active selection',
    category: 'action',
    parameters: [],
    examples: ['/delete', '/rm'],
    handler: async (args, component) => {
      if (!component) {
        return {
          success: false,
          action: 'delete',
          error: 'No component selected'
        };
      }
      return {
        success: true,
        action: 'delete',
        message: 'Component will be deleted'
      };
    }
  },

  {
    name: 'duplicate',
    aliases: ['dup'],
    description: 'Duplicate the active selection',
    category: 'action',
    parameters: [
      { name: 'offset', type: 'position', description: 'Optional offset x,y', required: false }
    ],
    examples: ['/duplicate', '/dup offset 20,20'],
    handler: async (args, component) => {
      if (!component) {
        return {
          success: false,
          action: 'add',
          error: 'No component selected'
        };
      }

      // Note: Position offset parameter is kept for API compatibility but
      // no longer affects placement in flex-only layout architecture
      const duplicated = {
        ...component,
        id: uuidv4(),
        name: `${component.name}-copy`,
      };

      return {
        success: true,
        action: 'add',
        component: duplicated,
        message: 'Component duplicated'
      };
    }
  },

  {
    name: 'clear',
    aliases: ['clr'],
    description: 'Clear content or styling',
    category: 'action',
    parameters: [
      { name: 'scope', type: 'string', description: 'content|style|all', required: false, defaultValue: 'all' }
    ],
    examples: ['/clear style', '/clr content'],
    handler: (args) => {
      const scope = args[0]?.toLowerCase() || 'all';
      if (scope === 'style') {
        return { style: {} };
      }
      if (scope === 'content') {
        return { properties: { content: '' } };
      }
      return { style: {}, properties: { content: '' } };
    }
  },

  // ===== DATA COMMAND =====
  // Note: Analytics queries feature temporarily unavailable (analytics-backend removed)
  {
    name: 'query',
    aliases: ['q'],
    description: 'Manage analytics bindings for table components (temporarily unavailable)',
    category: 'data',
    parameters: [
      { name: 'action', type: 'string', description: 'list|use', required: true },
      { name: 'args', type: 'string', description: 'Query identifier for use', required: false }
    ],
    examples: ['/query list', '/q use water-stats'],
    handler: async () => {
      return {
        success: false,
        action: 'unknown',
        error: 'Analytics query commands are temporarily unavailable.'
      };
    }
  },

  // ===== REFRESH BADGE COMMAND =====
  {
    name: 'refresh-badge',
    aliases: ['rb', 'live-badge'],
    description: 'Convert a flex-container badge into a live timestamp with click-to-refresh',
    category: 'data',
    parameters: [
      { name: 'scope', type: 'string', description: 'Container ID or name to scope refresh to (auto-detected if omitted)', required: false },
    ],
    examples: ['/refresh-badge', '/rb scope:barn-card'],
    handler: async (args, component, context) => {
      if (!component) {
        return { success: false, action: 'modify', error: 'No component selected' };
      }
      if (component.type !== ComponentType.FLEX_CONTAINER) {
        return { success: false, action: 'modify', error: 'Select a flex-container badge first' };
      }

      const editorState = useEditorStore.getState();
      const allComponents = editorState.components;

      // Find first TEXT or DATA_TEXT child inside this flex-container
      const children = allComponents.filter(c => c.parentId === component.id);
      const textChild = children.find(
        c => c.type === ComponentType.TEXT || c.type === ComponentType.DATA_TEXT,
      );
      if (!textChild) {
        return { success: false, action: 'modify', error: 'Badge container has no text child to convert' };
      }

      // Resolve scope: parse scope:name arg, or auto-detect by walking up parent chain
      let scopeContainerId = component.parentId || component.id;
      const scopeArg = args.find(a => a.startsWith('scope:'));
      if (scopeArg) {
        const scopeName = scopeArg.replace('scope:', '').trim();
        const scopeComponent = allComponents.find(
          c => c.id === scopeName ||
               (typeof c.properties?.label === 'string' && c.properties.label.toLowerCase() === scopeName.toLowerCase()),
        );
        if (scopeComponent) {
          scopeContainerId = scopeComponent.id;
        }
      } else {
        // Auto-detect: walk up parent chain to find nearest ancestor with data-bound descendants
        const DATA_TYPES = [
          ComponentType.DATA_TEXT, ComponentType.GAUGE_CHART, ComponentType.VERTICAL_GAUGE_CHART,
          ComponentType.DATA_CARD, ComponentType.LINE_CHART, ComponentType.BAR_CHART,
          ComponentType.PIE_CHART, ComponentType.AREA_CHART, ComponentType.SCATTER_CHART,
          ComponentType.HEATMAP_CHART, ComponentType.TREEMAP_CHART, ComponentType.MAP_CHART,
        ];
        let candidate = component.parentId;
        while (candidate) {
          const descendants = getComponentDescendants(candidate, allComponents);
          const hasData = descendants.some(c => DATA_TYPES.includes(c.type));
          if (hasData) {
            scopeContainerId = candidate;
            break;
          }
          const parent = allComponents.find(c => c.id === candidate);
          candidate = parent?.parentId;
        }
      }

      // Convert text child to DATA_TEXT with timestamp-aggregate source
      let textChildUpdate: {
        type?: ComponentType;
        properties: Record<string, unknown>;
      };
      if (textChild.type === ComponentType.TEXT) {
        const props: Record<string, unknown> = { ...(textChild.properties ?? {}) };
        const { content, ...otherProps } = props;
        textChildUpdate = {
          type: ComponentType.DATA_TEXT,
          properties: {
            ...otherProps,
            value: content ?? '',
            showLabel: false,
            emptyValueText: String(content || 'Never'),
            dataSource: { type: 'timestamp-aggregate' as const },
            refreshBadgeScopeId: scopeContainerId,
          },
        };
      } else {
        // Already DATA_TEXT — just update data source
        textChildUpdate = {
          properties: {
            ...(textChild.properties || {}),
            dataSource: { type: 'timestamp-aggregate' as const },
            refreshBadgeScopeId: scopeContainerId,
          },
        };
      }

      // Set refresh badge config on the flex-container
      const refreshBadge: RefreshBadgeConfig = {
        enabled: true,
        timestampComponentId: textChild.id,
        scopeContainerId,
        timestampFormat: 'relative',
      };

      return {
        success: true,
        action: 'modify',
        changes: {
          properties: {
            refreshBadge,
          },
        },
        additionalUpdates: [
          {
            componentId: textChild.id,
            changes: textChildUpdate,
            targetComponent: textChild,
          },
        ],
        message: `Badge configured: click to refresh data scoped to container ${scopeContainerId}`,
      };
    },
  },

  // ===== TEMPLATE COMMAND =====
  {
    name: 'template',
    aliases: ['tpl'],
    description: 'Instantiate a template layout',
    category: 'template',
    parameters: [
      { name: 'name', type: 'string', description: 'Template slug (hero-section|sidebar-layout|analytics-dashboard)', required: true }
    ],
    examples: ['/template hero-section', '/tpl sidebar-layout'],
    handler: async (args, _, context) => {
      const templateName = args.join(' ');

      const store = useEditorStore.getState();
      const project = store.project;
      const currentPage = store.currentPage;

      if (!project || !currentPage) {
        return {
          success: false,
          action: 'template',
          error: 'No active project or page'
        };
      }

      try {
        const instance = await templateInstantiationService.instantiateTemplate(
          templateName,
          project.id,
          currentPage.id,
          { x: 100, y: 100 }
        );

        if (!instance) {
          return {
            success: false,
            action: 'template',
            error: `Template not found: ${templateName}. Use /list-templates to see available options`
          };
        }

        return {
          success: true,
          action: 'template',
          components: instance.components,
          message: `Added ${instance.templateName} template`
        };
      } catch (error: unknown) {
        return {
          success: false,
          action: 'template',
          error: commandErrorMessage(error, 'Failed to add template')
        };
      }
    }
  },

  // ===== DEMO TEMPLATE COMMANDS =====
  ...DEMO_TEMPLATE_COMMAND_DEFINITIONS.map((definition) => ({
    name: definition.name,
    aliases: definition.aliases,
    description: definition.description,
    category: 'template' as const,
    parameters: [],
    examples: definition.examples,
    handler: makeTemplateCommand(
      definition.jsonPath,
      definition.displayName,
      { commandName: definition.name, ...definition.options },
    ),
  })),

  // ===== NAVIGATION COMMANDS =====
  {
    name: 'navigate',
    aliases: ['nav', 'goto'],
    description: 'Set button or clickable sub-component to navigate to a specific page',
    category: 'action',
    parameters: [
      {
        name: 'pageName',
        type: 'string',
        description: 'Name of the target page',
        required: true
      }
    ],
    examples: [
      '/navigate Dashboard',
      '/nav "Field Overview"',
      '/goto Analytics'
    ],
    handler: async (args: string[], component?: Component) => {
      // Check if component is a button OR a button-type sub-component
      const isButton = component?.type === ComponentType.BUTTON;
      const isButtonSubComponent = component?.isSubComponent === true &&
        component?.subComponentType === 'button';

      if (!component || (!isButton && !isButtonSubComponent)) {
        return commandError('Navigation can only be set on button components or clickable sub-components. Select a button first.');
      }

      if (args.length === 0) {
        return commandError('Please specify a page name: /navigate <page-name>');
      }

      const pageName = args.join(' ').replace(/["']/g, '');
      const editorStore = useEditorStore.getState();
      const pages = editorStore.pages || [];
      const currentPageId = editorStore.currentPageId;

      // Find page by name (exact match or partial)
      const targetPage = pages.find(
        p => p.name.toLowerCase() === pageName.toLowerCase()
      ) || pages.find(
        p => p.name.toLowerCase().includes(pageName.toLowerCase())
      );

      if (!targetPage) {
        const availablePages = pages
          .filter(p => p.id !== currentPageId)
          .map(p => p.name)
          .join(', ');
        return commandError(`Page "${pageName}" not found. Available pages: ${availablePages || 'none'}`);
      }

      // Check if navigation is allowed
      if (targetPage.navigation?.allowNavigation === false) {
        return commandError(`Navigation to page "${targetPage.name}" is not allowed`);
      }

      // Prevent navigation to current page
      if (targetPage.id === currentPageId) {
        return commandError('Cannot navigate to the current page');
      }

      const additionalUpdates = [];

      // If this is a sub-component, persist navigation to parent component properties
      // This ensures navigation works in viewer mode (sub-components aren't saved)
      if (component.isSubComponent && component.parentId) {
        const parentComponent = editorStore.getComponent(component.parentId);
        if (parentComponent) {
          // Map sub-component key to parent property name
          const subKey = component.id.split('::').pop();
          if (subKey === 'view-details') {
            additionalUpdates.push({
              componentId: component.parentId,
              changes: {
                properties: {
                  ...parentComponent.properties,
                  viewDetailsAction: 'navigate',
                  viewDetailsTarget: targetPage.id,
                },
              },
              targetComponent: parentComponent,
            });
            console.log('🔗 [/navigate] Also updated parent component properties:', {
              parentId: component.parentId,
              viewDetailsTarget: targetPage.id,
            });
          }
        }
      }

      return {
        success: true,
        action: 'modify',
        changes: {
          properties: {
            ...component.properties,
            action: 'navigate',
            target: targetPage.id,
            navigationLabel: targetPage.name
          }
        },
        additionalUpdates,
        message: `Button will navigate to "${targetPage.name}"`
      };
    }
  },
  {
    name: 'link',
    aliases: ['connect', 'link-to'],
    description: 'Link a button or clickable sub-component to navigate to a page. Works with regular buttons and sub-components (like "View Details" in FermentationTankCard)',
    category: 'action',
    parameters: [
      {
        name: 'pageName',
        type: 'string',
        description: 'Name of the target page to navigate to',
        required: true
      }
    ],
    examples: [
      '/link "Tank Details"',
      '/connect Dashboard',
      '/link-to "Field Overview"'
    ],
    handler: async (args: string[], component?: Component) => {
      // Check if component is a button OR a button-type sub-component
      const isButton = component?.type === ComponentType.BUTTON;
      const isButtonSubComponent = component?.isSubComponent === true &&
        component?.subComponentType === 'button';

      if (!component || (!isButton && !isButtonSubComponent)) {
        return commandError('Select a button or clickable element first (like "View Details" button in a card)');
      }

      if (args.length === 0) {
        return commandError('Please specify a page name: /link <page-name>');
      }

      const pageName = args.join(' ').replace(/["']/g, '');
      const editorStore = useEditorStore.getState();
      const pages = editorStore.pages || [];
      const currentPageId = editorStore.currentPageId;

      // Find page by name (exact match first, then partial)
      const targetPage = pages.find(
        p => p.name.toLowerCase() === pageName.toLowerCase()
      ) || pages.find(
        p => p.name.toLowerCase().includes(pageName.toLowerCase())
      );

      if (!targetPage) {
        const availablePages = pages
          .filter(p => p.id !== currentPageId)
          .map(p => p.name)
          .join(', ');
        return commandError(`Page "${pageName}" not found. Available pages: ${availablePages || 'none'}`);
      }

      // Check if navigation is allowed
      if (targetPage.navigation?.allowNavigation === false) {
        return commandError(`Navigation to page "${targetPage.name}" is not allowed`);
      }

      // Prevent navigation to current page
      if (targetPage.id === currentPageId) {
        return commandError('Cannot navigate to the current page');
      }

      const additionalUpdates = [];

      // If this is a sub-component, persist navigation to parent component properties
      // This ensures navigation works in viewer mode (sub-components aren't saved)
      if (component.isSubComponent && component.parentId) {
        const parentComponent = editorStore.getComponent(component.parentId);
        if (parentComponent) {
          // Map sub-component key to parent property name
          const subKey = component.id.split('::').pop();
          if (subKey === 'view-details') {
            additionalUpdates.push({
              componentId: component.parentId,
              changes: {
                properties: {
                  ...parentComponent.properties,
                  viewDetailsAction: 'navigate',
                  viewDetailsTarget: targetPage.id,
                },
              },
              targetComponent: parentComponent,
            });
            console.log('🔗 [/link] Also updated parent component properties:', {
              parentId: component.parentId,
              viewDetailsTarget: targetPage.id,
            });
          }
        }
      }

      const componentDesc = isButtonSubComponent
        ? `"${component.name}" sub-component`
        : 'button';

      return {
        success: true,
        action: 'modify',
        changes: {
          properties: {
            ...component.properties,
            action: 'navigate',
            target: targetPage.id,
            navigationLabel: targetPage.name
          }
        },
        additionalUpdates,
        message: `Linked ${componentDesc} to navigate to "${targetPage.name}"`
      };
    }
  },
  {
    name: 'list-pages',
    aliases: ['pages', 'show-pages'],
    description: 'List all available pages in the current project',
    category: 'action',
    examples: ['/list-pages', '/pages'],
    handler: async () => {
      const editorStore = useEditorStore.getState();
      const pages = editorStore.pages || [];
      const currentPageId = editorStore.currentPageId;

      if (pages.length === 0) {
        return commandMessage('No pages in current project');
      }

      const pageList = pages
        .sort((a, b) => a.order - b.order)
        .map(p => {
          const indicators = [];
          if (p.isDefault) indicators.push('default');
          if (p.id === currentPageId) indicators.push('current');
          const suffix = indicators.length > 0 ? ` (${indicators.join(', ')})` : '';
          return `• ${p.name}${suffix}`;
        })
        .join('\n');

      return commandMessage(`Available pages:\n${pageList}`);
    }
  },

  // ===== SKILL COMMANDS =====
  {
    name: 'skill',
    aliases: ['sk'],
    description: 'Execute a skill in the visual editor',
    category: 'action',
    parameters: [{ name: 'name', type: 'string' as const, description: 'Skill name or search query', required: true }],
    examples: ['/skill deploy-chart', '/sk weekly-report'],
    handler: async () => {
      // Stub — actual execution is handled by the /skill intercept in AIPropertiesChat
      return commandMessage('Use /skill <name> to execute a skill. Open the Skills panel to browse available skills.');
    },
  },
];
